import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, GearSix } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canManage } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_configuracion_seen";

function tourSteps(user) {
  const steps = [];
  if (canManage(user)) {
    steps.push({ target: "[data-tour='cfg-nuevo']", title: "Nueva configuración normativa", content: "Define jornada máxima semanal, topes de horas extra diario/semanal y prima dominical conforme la normativa vigente." });
  }
  steps.push({ target: "[data-tour='cfg-grid']", title: "Configuraciones vigentes", content: "Cada tarjeta representa una configuración normativa con su fecha de vigencia. Solo una suele estar 'Activa' a la vez." });
  return steps;
}

export default function Configuracion() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/configuracion"), api.get("/empresas")]);
    setItems(c.data);
    setEmpresas(e.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm({
      empresa_id: empresas[0]?.id || "",
      nombre: "",
      jornada_max_semanal: 48,
      limite_horas_extra_diario: 3,
      limite_horas_extra_semanal: 9,
      prima_dominical_pct: 25,
      tipos_jornada_permitidos: ["diurna", "nocturna", "mixta"],
      campos_obligatorios: ["hora_entrada", "hora_salida", "fecha", "empleado_id"],
      fecha_vigencia: hoyISO(),
      excepciones: "",
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/configuracion", form);
      toast.success("Configuración creada");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="configuracion-page">
      <PageHeader
        kicker="Parametrización"
        title="Configuración Normativa"
        subtitle="Ajusta jornada máxima, topes de horas extra y reglas conforme la STPS publique disposiciones definitivas."
        actions={
          <div className="flex gap-2 items-center">
            {canManage(user) && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="cfg-new-btn" data-tour="cfg-nuevo">
                <Plus size={16} weight="bold" className="mr-2" /> Nueva configuración
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="grid md:grid-cols-2 gap-4" data-tour="cfg-grid">
        {items.length === 0 && <div className="md:col-span-2 text-center py-12 border border-dashed border-slate-300 rounded-md text-slate-500">Sin configuración</div>}
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-2">
                <GearSix size={20} weight="duotone" className="text-slate-700 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold text-lg text-ink">{c.nombre}</h3>
                  <div className="text-xs text-slate-500">Vigencia: {c.fecha_vigencia?.slice(0, 10)}</div>
                </div>
              </div>
              {c.activo && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded">Activa</span>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="label-uppercase">Jornada máx semanal</div>
                <div className="font-display font-bold text-2xl">{c.jornada_max_semanal}h</div>
              </div>
              <div>
                <div className="label-uppercase">Tope H. Extra diario</div>
                <div className="font-display font-bold text-2xl">{c.limite_horas_extra_diario}h</div>
              </div>
              <div>
                <div className="label-uppercase">Tope H. Extra semanal</div>
                <div className="font-display font-bold text-2xl">{c.limite_horas_extra_semanal}h</div>
              </div>
              <div>
                <div className="label-uppercase">Prima dominical</div>
                <div className="font-display font-bold text-2xl">{c.prima_dominical_pct}%</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 text-xs">
              <div className="label-uppercase mb-1">Tipos jornada permitidos</div>
              <div className="flex flex-wrap gap-1">
                {(c.tipos_jornada_permitidos || []).map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-slate-100 rounded text-slate-700">{t}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Nueva configuración normativa</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">Nombre</Label><Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Reforma 2026 Q1" /></div>
            <div className="space-y-1"><Label className="label-uppercase">Jornada máx semanal (h)</Label><Input type="number" value={form.jornada_max_semanal || 48} onChange={(e) => setForm({ ...form, jornada_max_semanal: parseInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Tope extra diario</Label><Input type="number" value={form.limite_horas_extra_diario || 3} onChange={(e) => setForm({ ...form, limite_horas_extra_diario: parseInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Tope extra semanal</Label><Input type="number" value={form.limite_horas_extra_semanal || 9} onChange={(e) => setForm({ ...form, limite_horas_extra_semanal: parseInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Prima dominical %</Label><Input type="number" step="0.5" value={form.prima_dominical_pct || 25} onChange={(e) => setForm({ ...form, prima_dominical_pct: parseFloat(e.target.value) })} /></div>
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">Vigencia</Label><Input type="date" value={form.fecha_vigencia || ""} onChange={(e) => setForm({ ...form, fecha_vigencia: e.target.value })} /></div>
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">Excepciones</Label><Input value={form.excepciones || ""} onChange={(e) => setForm({ ...form, excepciones: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
